import type { MediaFormat } from '@prisma/client'
import { XIcon } from 'lucide-react'
import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useMemo,
} from 'react'

import { Slot } from '~/ui/slot'
import { cn } from '~/utils/classnames'

const MediaCardContext = createContext<{ format: MediaFormat } | null>(null)

const useMediaCardContext = () => {
  const context = useContext(MediaCardContext)

  if (!context) {
    throw new Error(
      'useMediaCardContext must be used within a MediaCardProvider'
    )
  }

  return context
}

interface MediaCardProps extends React.HTMLAttributes<HTMLDivElement> {
  format: MediaFormat
  link?: string | undefined
  asChild?: boolean
}

const MediaCardRoot = ({
  format,
  className,
  children,
  asChild,
  ...props
}: MediaCardProps) => {
  const context = useMemo(() => ({ format }), [format])

  const Component = asChild ? Slot : 'div'
  return (
    <MediaCardContext.Provider value={context}>
      <Component
        className={cn('group @container/media-card relative h-max', className)}
        {...props}
      >
        {children}
      </Component>
    </MediaCardContext.Provider>
  )
}

const MediaCardCover = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<'div'>) => {
  const { format } = useMediaCardContext()

  return (
    <figure
      className={cn(
        'bg-foreground/8 relative aspect-square w-full overflow-hidden rounded-md transition-[aspect-ratio,box-shadow]',
        '*:rounded-inherit *:absolute *:object-cover',
        // '[&>*:not(.MediaCard-cover-control)]:inset-0 [&>*:not(.MediaCard-cover-control)]:h-full [&>*:not(.MediaCard-cover-control)]:w-full',
        format === 'portrait' && 'aspect-[3/4]',
        format === 'landscape' && 'aspect-[4/3]',
        className
      )}
      {...props}
    >
      {children}
    </figure>
  )
}

interface MediaCardCoverControlProps extends React.HTMLAttributes<HTMLDivElement> {
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
}

const MediaCardCoverControl = ({
  position,
  className,
  children,
  onClick,
  ...props
}: MediaCardCoverControlProps) => {
  const enhancedChildren = Children.map(children, (child) => {
    if (isValidElement(child)) {
      const childProps = child.props as { className?: string }
      return cloneElement(
        child as React.ReactElement<{
          className?: string
          'data-position'?: string
        }>,
        {
          className: cn('overlay-hover-control', childProps.className),
          'data-position': position,
        }
      )
    }
    return child
  })

  return (
    <div
      className={cn('absolute z-10 flex items-center gap-2', className)}
      data-position={position}
      // Prevents the click from bubbling up to the MediaCard
      // useful when the MediaCard is wrapped in a Link — you don't want the link to be triggered when clicking on the control
      onClick={(e) => {
        e.stopPropagation()
        onClick?.(e)
      }}
      {...props}
    >
      {enhancedChildren}
    </div>
  )
}

const MediaCardContent = ({
  className,
  children,
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn('text-foreground mt-2 text-sm', className)}
      // Prevents the click from bubbling up to the MediaCard
      // useful when the MediaCard is wrapped in a Link — you don't want the link to be triggered when clicking on the control
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
    >
      {children}
    </div>
  )
}

const MediaCardTitle = ({
  className,
  children,
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn(
        'line-clamp-1 flex items-center gap-2 truncate py-px font-medium *:truncate',
        className
      )}
    >
      {children}
    </div>
  )
}

const MediaCardDescription = ({
  className,
  children,
  asChild,
}: React.HTMLAttributes<HTMLDivElement> & { asChild?: boolean }) => {
  const Component = asChild ? Slot : 'p'
  return <Component className={cn(className)}>{children}</Component>
}

const MediaCardCloseButton = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      className="bg-under/80 text-highlight touch:opacity-100 touch:scale-100 absolute -top-2 -right-2 z-20 scale-0 cursor-pointer rounded-full p-1 opacity-0 transition group-hover:scale-100 group-hover:opacity-100 focus:scale-100 focus:opacity-100"
      {...props}
    >
      {children ?? <XIcon className="size-3" />}
    </button>
  )
}

const MediaCardRing = ({
  className,
  isActive,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  isActive?: boolean
}) => {
  return (
    <div
      className={cn(
        'ring-accent-element absolute inset-0 z-10 rounded-md ring-0 transition group-hover:ring-4',
        'cursor-pointer',
        isActive && 'ring-4',
        className
      )}
      {...props}
    />
  )
}

const MediaCard = Object.assign(MediaCardRoot, {
  Cover: MediaCardCover,
  CoverControl: MediaCardCoverControl,
  Content: MediaCardContent,
  Title: MediaCardTitle,
  Description: MediaCardDescription,
  CloseButton: MediaCardCloseButton,
  Ring: MediaCardRing,
})

export { MediaCard }
